import { useCallback, useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  Popover,
  Row,
  Spin,
  Typography,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  LeftOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { getDashboardCalendar } from "../../services/dashboardApi";

const { Title, Text } = Typography;

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

// Named-colour fallback. The API sends raw hex, which passes straight through.
const DOT = {
  green: "#22c55e",
  red: "#ef4444",
  orange: "#f97316",
  blue: "#3b82f6",
};

function resolveColor(color) {
  return DOT[color] || color || "#586888";
}

function pad(n) {
  return String(n).padStart(2, "0");
}

function buildCells(year, month) {
  const first = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prevDays = new Date(year, month, 0).getDate();
  const cells = [];
  for (let i = first - 1; i >= 0; i--)
    cells.push({ day: prevDays - i, cur: false });
  for (let d = 1; d <= daysInMonth; d++) cells.push({ day: d, cur: true });
  while (cells.length % 7 !== 0)
    cells.push({ day: cells.length - daysInMonth - first + 1, cur: false });
  return cells;
}

function chunk(arr, size) {
  const out = [];
  for (let i = 0; i < arr.length; i += size) out.push(arr.slice(i, i + size));
  return out;
}

// Descriptions sometimes carry a trailing signature ("…text\n\t-admin_realtekh").
// Split that off so we can show the body and the author separately.
function parseDescription(desc) {
  if (!desc) return { text: "", author: null };
  const m = desc.match(/\n\s*-\s*([^\n]+)\s*$/);
  if (m) return { text: desc.slice(0, m.index).trim(), author: m[1].trim() };
  return { text: desc.trim(), author: null };
}

// API days -> events keyed by "YYYY-MM-DD"; each event keeps the fields the
// popover needs (color/title/description/author).
function buildEvents(days = []) {
  const map = {};
  days.forEach((day) => {
    map[day.date] = (day.events || []).map((e) => {
      const { text, author } = parseDescription(e.description);
      return { color: e.color, label: e.label, title: e.title, text, author };
    });
  });

  return map;
}

// One popover per day listing every event for that date.
function eventsPopover(events) {
  return (
    <div
      className="calendar-popover"
      style={{ maxWidth: 280, maxHeight: 260, overflowY: "auto" }}
    >
      {events.map((e, i) => (
        <div
          key={i}
          style={{
            display: "flex",
            gap: 8,
            padding: "6px 0",
            borderBottom: i < events.length - 1 ? "1px solid #f0f0f0" : "none",
          }}
        >
          <span
            className="calendar-dot"
            style={{
              background: resolveColor(e.color),
              marginTop: 6,
              flex: "0 0 auto",
            }}
          />
          <div style={{ minWidth: 0 }}>
            <Text strong style={{ fontSize: 13, display: "block" }}>
              {e.title || e.label}
            </Text>
            {e.text && (
              <Text
                style={{
                  fontSize: 12,
                  color: "#555",
                  whiteSpace: "pre-line",
                  wordBreak: "break-word",
                }}
              >
                {e.text}
              </Text>
            )}
            {e.author && (
              <Text
                style={{
                  fontSize: 11,
                  color: "#999",
                  display: "block",
                  marginTop: 2,
                }}
              >
                — {e.author}
              </Text>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function CalendarCard({ userIds = [] }) {
  const today = new Date();

  const [view, setView] = useState({
    year: today.getFullYear(),
    month: today.getMonth(),
  });
  const [events, setEvents] = useState({});
  const [legend, setLegend] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activePopover, setActivePopover] = useState(null);

  const userKey = JSON.stringify(userIds || []);

  const fetchCalendarData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDashboardCalendar({ userIds: userIds || [] });
      if (res && res.status && res.data) {
        setLegend(res.data.legend || []);
        setEvents(buildEvents(res.data.days || []));
      } else {
        setLegend([]);
        setEvents({});
      }
    } catch (error) {
      console.error("Error fetching calendar data:", error);
    } finally {
      setLoading(false);
    }
    // userIds read via userKey to keep a stable dependency
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userKey]);

  useEffect(() => {
    fetchCalendarData();
  }, [fetchCalendarData]);

  // Allow other parts of the app to ask the calendar to reload.
  useEffect(() => {
    window.addEventListener("dashboard-calendar:refresh", fetchCalendarData);
    return () =>
      window.removeEventListener(
        "dashboard-calendar:refresh",
        fetchCalendarData,
      );
  }, [fetchCalendarData]);

  const weeks = chunk(buildCells(view.year, view.month), 7);
  const prev = () =>
    setView((v) =>
      v.month === 0
        ? { year: v.year - 1, month: 11 }
        : { ...v, month: v.month - 1 },
    );
  const next = () =>
    setView((v) =>
      v.month === 11
        ? { year: v.year + 1, month: 0 }
        : { ...v, month: v.month + 1 },
    );

  return (
    <Card
      className="calendar-card"
      title={
        <div className="calendar-header-title">
          <CalendarOutlined />
          <Title level={5}>
            {MONTHS[view.month]} {view.year}
          </Title>
        </div>
      }
      extra={
        <div className="calendar-nav">
          <Tooltip title="Previous month">
            <Button
              type="text"
              icon={<LeftOutlined />}
              size="small"
              onClick={prev}
            />
          </Tooltip>
          <Tooltip title="Next month">
            <Button
              type="text"
              icon={<RightOutlined />}
              size="small"
              onClick={next}
            />
          </Tooltip>
        </div>
      }
    >
      <Spin spinning={loading}>
        <Row gutter={[6, 0]} className="calendar-day-names">
          {DAYS.map((d) => (
            <Col key={d} flex="1" className="calendar-day-name">
              {d}
            </Col>
          ))}
        </Row>

        <div className="calendar-weeks">
          {weeks.map((week, wi) => (
            <Row
              key={wi}
              gutter={[6, 0]}
              align="top"
              className="calendar-week-row"
            >
              {week.map((c, ci) => {
                const dateKey = c.cur
                  ? `${view.year}-${pad(view.month + 1)}-${pad(c.day)}`
                  : null;
                const dayEvents = dateKey ? events[dateKey] || [] : [];
                // Up to 3 distinct-colour dots; the popover lists every event.
                const dotColors = [
                  ...new Set(dayEvents.map((e) => e.color)),
                ].slice(0, 3);
                const isToday =
                  c.cur &&
                  c.day === today.getDate() &&
                  view.month === today.getMonth() &&
                  view.year === today.getFullYear();

                return (
                  <Col key={ci} flex="1">
                    <div
                      className={`calendar-cell${isToday ? " today" : ""}${!c.cur ? " faded" : ""}`}
                    >
                      <div className="calendar-cell-inner">
                        <Text className="calendar-cell-number">{c.day}</Text>
                        {dayEvents.length > 0 && (
                          <Popover
                            open={activePopover === dateKey}
                            trigger="click"
                            content={eventsPopover(dayEvents)}
                            placement="topRight"
                            destroyTooltipOnHide
                            onOpenChange={(visible) => {
                              if (visible) {
                                setActivePopover(dateKey);
                              } else {
                                setActivePopover(null);
                              }
                            }}
                          >
                            <div className="calendar-cell-dots">
                              {dotColors.map((color, j) => (
                                <span
                                  key={j}
                                  className="calendar-dot"
                                  style={{ background: resolveColor(color) }}
                                />
                              ))}
                            </div>
                          </Popover>
                        )}
                      </div>
                    </div>
                  </Col>
                );
              })}
            </Row>
          ))}
        </div>

        <div className="calendar-legend">
          {legend.map(({ label, color }) => (
            <div key={label} className="calendar-legend-item">
              <span
                className="calendar-legend-dot"
                style={{ background: resolveColor(color) }}
              />
              <Text>{label}</Text>
            </div>
          ))}
        </div>
      </Spin>
    </Card>
  );
}
